import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button, Spin, Alert, Empty, Tag, Input, Modal, message } from 'antd';
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';

import { professorService } from '../../services/api';
import { buildApiErrorMessage } from '../../utils/apiError';
import '../../styles/PortalProfessor.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

// ─────────────────────────────────────────────────────────────
// Contrato real (backend, Fase 10): ChamadaProfessorResponse NAO inclui
// nome_turm/modalidade_nome (esses campos so existem em
// EncontroProfessorResponse, da listagem -- Fase 11-B). Este cabecalho
// so pode mostrar data/horario/status/contadores. Nao alterar backend
// nesta fase; PortalProfessorHome.js/EncontroCard.js (onde o card ja
// exibe nome_turm/modalidade_nome) nao estao autorizados a alteracao
// nesta fase, entao nao ha como repassar esses campos via router state
// sem tocar em arquivo fora do escopo autorizado. Documentado no
// relatorio da fase como limitacao conhecida, nao corrigida aqui.
// ─────────────────────────────────────────────────────────────

const STATUS_ENCONTRO_INFO = {
  PREVISTA: { label: 'Chamada pendente', color: 'gold', icon: <ClockCircleOutlined /> },
  REALIZADA: { label: 'Chamada realizada', color: 'green', icon: <CheckCircleOutlined /> },
  CANCELADA: { label: 'Aula cancelada', color: 'default', icon: <StopOutlined /> },
};

const STATUS_ALUNO_OPCOES = [
  { value: 'PRESENTE', label: 'Presente', color: '#389e0d' },
  { value: 'AUSENTE', label: 'Ausente', color: '#cf1322' },
  { value: 'JUSTIFICADA', label: 'Justificada', color: '#d48806' },
];

const MENSAGEM_SEM_VINCULO =
  'Seu usuário ainda não está vinculado a um professor/equipe. Procure a administração.';
const MENSAGEM_NAO_ENCONTRADO = 'Esta aula não está disponível.';

const formatarHora = (valor) => (valor ? valor.slice(0, 5) : '--:--');

const formatarDataHora = (valor) => {
  if (!valor) return null;
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return valor;
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

const normalizarObservacao = (valor) => {
  if (valor == null) return null;
  const limpa = valor.trim();
  return limpa === '' ? null : limpa;
};

const paraAlunoLocal = (aluno) => ({
  matricula_id: aluno.matricula_id,
  aluno_id: aluno.aluno_id,
  nome_completo: aluno.nome_completo,
  status_frequencia: aluno.status_frequencia,
  observacao: aluno.observacao,
});

// ─────────────────────────────────────────────────────────────
// Linha de um aluno na chamada
// ─────────────────────────────────────────────────────────────

const AlunoLinha = ({ aluno, somenteLeitura, observacaoAberta, onMudarStatus, onMudarObservacao, onAbrirObservacao }) => {
  const naoInformado = aluno.status_frequencia === 'NAO_INFORMADA';
  const inputId = `observacao-${aluno.matricula_id}`;

  return (
    <div className="portal-professor__aluno-linha" data-testid="aluno-linha">
      <div className="portal-professor__aluno-nome">
        <Text strong id={`aluno-nome-${aluno.matricula_id}`}>
          {aluno.nome_completo}
        </Text>
        {naoInformado && (
          <Tag color="default" style={{ marginLeft: 8 }}>
            Não informado
          </Tag>
        )}
      </div>

      {somenteLeitura ? (
        <Tag
          color={STATUS_ALUNO_OPCOES.find((o) => o.value === aluno.status_frequencia)?.color || 'default'}
        >
          {STATUS_ALUNO_OPCOES.find((o) => o.value === aluno.status_frequencia)?.label ||
            (naoInformado ? 'Não informado' : aluno.status_frequencia)}
        </Tag>
      ) : (
        <>
          <div
            className="portal-professor__status-opcoes"
            role="group"
            aria-labelledby={`aluno-nome-${aluno.matricula_id}`}
          >
            {STATUS_ALUNO_OPCOES.map((opcao) => {
              const selecionado = aluno.status_frequencia === opcao.value;
              return (
                <Button
                  key={opcao.value}
                  className="portal-professor__status-btn"
                  aria-pressed={selecionado}
                  aria-label={`Marcar ${aluno.nome_completo} como ${opcao.label}`}
                  type={selecionado ? 'primary' : 'default'}
                  style={
                    selecionado
                      ? { backgroundColor: opcao.color, borderColor: opcao.color }
                      : undefined
                  }
                  onClick={() => onMudarStatus(aluno.matricula_id, opcao.value)}
                >
                  {opcao.label}
                </Button>
              );
            })}
          </div>

          {observacaoAberta ? (
            <TextArea
              id={inputId}
              aria-label={`Observação sobre ${aluno.nome_completo}`}
              value={aluno.observacao || ''}
              onChange={(e) => onMudarObservacao(aluno.matricula_id, e.target.value)}
              placeholder="Observação (opcional)"
              autoSize={{ minRows: 1, maxRows: 4 }}
              maxLength={500}
              style={{ marginTop: 8 }}
            />
          ) : (
            <Button
              type="link"
              size="small"
              onClick={() => onAbrirObservacao(aluno.matricula_id)}
              style={{ paddingLeft: 0, marginTop: 4 }}
            >
              + Adicionar observação
            </Button>
          )}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Página
// ─────────────────────────────────────────────────────────────

const ChamadaDetalhe = () => {
  const { encontroId } = useParams();
  const navigate = useNavigate();

  const [chamada, setChamada] = useState(null); // baseline do servidor (GET/PUT)
  const [alunosLocal, setAlunosLocal] = useState([]);
  const [observacoesAbertas, setObservacoesAbertas] = useState(() => new Set());

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null); // erro de carregamento (GET)
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState(null); // { status, message }
  const [precisaAtualizarAntes, setPrecisaAtualizarAntes] = useState(false); // 409

  const sincronizarComServidor = useCallback((dadosChamada) => {
    setChamada(dadosChamada);
    const alunos = (dadosChamada.alunos || []).map(paraAlunoLocal);
    setAlunosLocal(alunos);
    setObservacoesAbertas(
      new Set(alunos.filter((a) => a.observacao && a.observacao.trim() !== '').map((a) => a.matricula_id))
    );
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    setErroSalvar(null);
    setPrecisaAtualizarAntes(false);
    try {
      const response = await professorService.getChamada(encontroId);
      sincronizarComServidor(response.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setErro({ status, message: MENSAGEM_SEM_VINCULO });
      } else if (status === 404) {
        setErro({ status, message: MENSAGEM_NAO_ENCONTRADO });
      } else {
        const { message: mensagem } = buildApiErrorMessage(
          err,
          'Não foi possível carregar a chamada. Tente novamente.'
        );
        setErro({ status, message: mensagem });
      }
    } finally {
      setLoading(false);
    }
  }, [encontroId, sincronizarComServidor]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // ── Derivados do state local (nunca dos contadores antigos do GET) ──
  const contadores = alunosLocal.reduce(
    (acc, a) => {
      if (a.status_frequencia === 'PRESENTE') acc.presentes += 1;
      else if (a.status_frequencia === 'AUSENTE') acc.ausentes += 1;
      else if (a.status_frequencia === 'JUSTIFICADA') acc.justificadas += 1;
      else acc.naoInformadas += 1;
      return acc;
    },
    { presentes: 0, ausentes: 0, justificadas: 0, naoInformadas: 0 }
  );
  const chamadaCompleta = contadores.naoInformadas === 0;

  const baselineAlunos = chamada ? (chamada.alunos || []).map(paraAlunoLocal) : [];
  const baselineByMatricula = new Map(baselineAlunos.map((a) => [a.matricula_id, a]));
  const houveAlteracao = alunosLocal.some((local) => {
    const base = baselineByMatricula.get(local.matricula_id);
    if (!base) return true;
    return (
      local.status_frequencia !== base.status_frequencia ||
      normalizarObservacao(local.observacao) !== normalizarObservacao(base.observacao)
    );
  });

  const statusEncontro = chamada?.status_encontro;
  const cancelada = statusEncontro === 'CANCELADA';

  // ── Handlers de edição (somente state local, sem chamada de rede) ──
  const handleMudarStatus = (matriculaId, novoStatus) => {
    setAlunosLocal((atual) =>
      atual.map((a) => (a.matricula_id === matriculaId ? { ...a, status_frequencia: novoStatus } : a))
    );
  };

  const handleMudarObservacao = (matriculaId, texto) => {
    setAlunosLocal((atual) =>
      atual.map((a) => (a.matricula_id === matriculaId ? { ...a, observacao: texto } : a))
    );
  };

  const handleAbrirObservacao = (matriculaId) => {
    setObservacoesAbertas((atual) => new Set(atual).add(matriculaId));
  };

  const handleMarcarTodosPresentes = () => {
    // So preenche quem esta NAO_INFORMADA -- nunca sobrescreve
    // AUSENTE/JUSTIFICADA/PRESENTE ja marcados pelo professor.
    setAlunosLocal((atual) =>
      atual.map((a) => (a.status_frequencia === 'NAO_INFORMADA' ? { ...a, status_frequencia: 'PRESENTE' } : a))
    );
  };

  // ── Salvamento ──
  const handleSalvar = async () => {
    if (salvando) return; // impede duplo clique
    // Defesa programática: nunca envia PUT com algum aluno ainda
    // NAO_INFORMADA (schema HTTP só aceita PRESENTE/AUSENTE/JUSTIFICADA) --
    // independe do botão estar desabilitado, protege contra chamadas
    // programáticas/race de state.
    if (!chamadaCompleta) return;
    setSalvando(true);
    setErroSalvar(null);
    try {
      const registros = alunosLocal.map((a) => ({
        matricula_id: a.matricula_id,
        status: a.status_frequencia,
        observacao: normalizarObservacao(a.observacao),
      }));
      const response = await professorService.salvarChamada(encontroId, registros);
      sincronizarComServidor(response.data);
      setPrecisaAtualizarAntes(false);
      message.success(
        response.data.status_encontro === 'REALIZADA' && chamada?.status_encontro === 'PREVISTA'
          ? 'Chamada finalizada com sucesso.'
          : 'Alterações salvas com sucesso.'
      );
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setErroSalvar({ status, message: MENSAGEM_SEM_VINCULO });
      } else if (status === 404) {
        setErroSalvar({ status, message: MENSAGEM_NAO_ENCONTRADO });
      } else if (status === 422) {
        const { message: mensagem } = buildApiErrorMessage(
          err,
          'Não foi possível salvar a chamada. Revise os dados e tente novamente.'
        );
        setErroSalvar({ status, message: mensagem, podeAtualizar: true });
      } else if (status === 409) {
        const { message: mensagem } = buildApiErrorMessage(
          err,
          'Não foi possível salvar por conflito de dados.'
        );
        setErroSalvar({ status, message: mensagem, podeAtualizar: true });
        setPrecisaAtualizarAntes(true);
      } else {
        const { message: mensagem } = buildApiErrorMessage(
          err,
          'Não foi possível salvar a chamada. Tente novamente.'
        );
        setErroSalvar({ status, message: mensagem });
      }
    } finally {
      setSalvando(false);
    }
  };

  // ── Navegação ──
  const voltarParaLista = () => navigate('/professor');

  const handleVoltar = () => {
    if (!cancelada && houveAlteracao) {
      Modal.confirm({
        title: 'Descartar alterações?',
        content: 'Você fez alterações na chamada que ainda não foram salvas. Deseja realmente sair sem salvar?',
        okText: 'Sair sem salvar',
        cancelText: 'Continuar editando',
        okButtonProps: { danger: true },
        onOk: voltarParaLista,
      });
      return;
    }
    voltarParaLista();
  };

  // ── Render ──
  const statusInfo = STATUS_ENCONTRO_INFO[statusEncontro] || { label: statusEncontro, color: 'default', icon: null };

  const botaoPrincipalTexto = statusEncontro === 'PREVISTA' ? 'Finalizar chamada' : 'Salvar alterações';
  // Qualquer PUT precisa de payload 100% preenchido (PRESENTE/AUSENTE/
  // JUSTIFICADA para todos) -- isso vale tanto para o primeiro fechamento
  // quanto para correções em REALIZADA. Em REALIZADA, alem disso so faz
  // sentido habilitar se algo realmente mudou.
  const botaoPrincipalDesabilitado =
    salvando ||
    precisaAtualizarAntes ||
    !chamadaCompleta ||
    (statusEncontro !== 'PREVISTA' && !houveAlteracao);

  return (
    <div className="portal-professor">
      <Button
        icon={<ArrowLeftOutlined />}
        type="text"
        onClick={handleVoltar}
        aria-label="Voltar para suas aulas"
        style={{ marginBottom: 12 }}
      >
        Voltar
      </Button>

      {loading && (
        <div className="portal-professor__state" role="status" aria-live="polite">
          <Spin size="large" />
          <Text type="secondary">Carregando chamada...</Text>
        </div>
      )}

      {!loading && erro && (
        <div className="portal-professor__state">
          <Alert
            type={erro.status === 403 || erro.status === 404 ? 'warning' : 'error'}
            showIcon
            message="Não foi possível abrir a chamada"
            description={erro.message}
            style={{ maxWidth: 480, textAlign: 'left' }}
          />
          {erro.status !== 403 && erro.status !== 404 && (
            <Button icon={<ReloadOutlined />} onClick={carregar}>
              Tentar novamente
            </Button>
          )}
        </div>
      )}

      {!loading && !erro && chamada && (
        <div className="portal-professor__list">
          <div className="portal-professor__chamada-header">
            <Title level={4} style={{ marginBottom: 4 }}>
              Chamada
            </Title>
            <Text type="secondary">
              {chamada.data} • {formatarHora(chamada.hora_inicio)}–{formatarHora(chamada.hora_fim)}
            </Text>
            <div style={{ marginTop: 8 }}>
              <Tag icon={statusInfo.icon} color={statusInfo.color}>
                {statusInfo.label}
              </Tag>
              {chamada.chamada_fechada_em && (
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                  Fechada em {formatarDataHora(chamada.chamada_fechada_em)}
                </Text>
              )}
            </div>
            <div className="portal-professor__contadores" aria-live="polite">
              <Text>Presentes: {contadores.presentes}</Text>
              <Text>Ausentes: {contadores.ausentes}</Text>
              <Text>Justificadas: {contadores.justificadas}</Text>
              <Text>Não informadas: {contadores.naoInformadas}</Text>
            </div>
          </div>

          {cancelada && (
            <Alert
              type="default"
              showIcon
              icon={<StopOutlined />}
              message="Aula cancelada"
              description="Esta aula foi cancelada. A chamada não pode ser editada."
              style={{ maxWidth: 480 }}
            />
          )}

          {erroSalvar && (
            <Alert
              type={erroSalvar.status === 403 || erroSalvar.status === 404 ? 'warning' : 'error'}
              showIcon
              message="Não foi possível salvar"
              description={erroSalvar.message}
              action={
                erroSalvar.podeAtualizar ? (
                  <Button size="small" icon={<ReloadOutlined />} onClick={carregar}>
                    Atualizar chamada
                  </Button>
                ) : undefined
              }
            />
          )}

          {!cancelada && !chamadaCompleta && (
            <Alert
              type="info"
              showIcon
              message={`${contadores.naoInformadas} aluno${contadores.naoInformadas > 1 ? 's' : ''} ainda sem presença informada.`}
            />
          )}

          {!cancelada && statusEncontro === 'PREVISTA' && contadores.naoInformadas > 0 && (
            <Button onClick={handleMarcarTodosPresentes} style={{ alignSelf: 'flex-start' }}>
              Marcar todos como presentes
            </Button>
          )}

          {alunosLocal.length === 0 ? (
            <Empty description="Nenhum aluno elegível para esta aula." />
          ) : (
            <div className="portal-professor__alunos-lista">
              {alunosLocal.map((aluno) => (
                <AlunoLinha
                  key={aluno.matricula_id}
                  aluno={aluno}
                  somenteLeitura={cancelada}
                  observacaoAberta={observacoesAbertas.has(aluno.matricula_id)}
                  onMudarStatus={handleMudarStatus}
                  onMudarObservacao={handleMudarObservacao}
                  onAbrirObservacao={handleAbrirObservacao}
                />
              ))}
            </div>
          )}

          {!cancelada && (
            <Button
              type="primary"
              size="large"
              block
              loading={salvando}
              disabled={botaoPrincipalDesabilitado}
              onClick={handleSalvar}
              style={{ minHeight: 48 }}
            >
              {botaoPrincipalTexto}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ChamadaDetalhe;
